import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { string, number, shape, arrayOf, oneOfType, func, bool } from 'prop-types';
import { Icon } from '@magalu/stereo-ui-icons';
import { Container, Content, ImageContainer, Title, ButtonPreviewContainer, ButtonPreview, MagaluIndica, MercadoFull, SponsoredEl } from './product-card.styles';
import { OfferTimer, Badge, ProductShipping, AlertText, TagCoupon } from '../../molecules';
import { AttributeList } from '..';
import Image from '../../atoms/ResponsiveImage';
import { LIST, MINIATURE, MAGALU_DOMAIN } from '../../constants';
import CardHeader from './CardHeader';
import GeoLocTags from './GeoLocTags';
import renderReviewVariations from './ReviewVariations';
import AllGeoLocTags from './AllGeoLocTags';
import ProductCardShippingTag from './ShippingTags/shipping-tags';
import Loyalty from './Loyalty';
import ProductCardPrice from '../ProductCardPrice';

const parseUrl = ({
  domain,
  url,
  adsSellerId
}) => {
  const isFullURL = /^https?:\/\//.test(url);
  const productUrl = new URL(isFullURL ? url : domain + url);

  if (adsSellerId) {
    productUrl.searchParams.append('seller_id', adsSellerId);
  }

  const absoluteUrl = productUrl.toString();
  const href = isFullURL ? absoluteUrl : absoluteUrl.replace(productUrl.origin, '');
  return {
    absoluteUrl,
    href
  };
};

const ProductCard = _ref => {
  let {
    hideLinkBorder,
    onClick,
    onMouseDown,
    onView,
    domain,
    imgLazyLoading,
    index,
    lazyLoadingOffset,
    showInCashInfo,
    enableShadow,
    enableHover,
    geoLocTags,
    allGeoLocTags,
    hidePriceMode,
    hidePrice,
    hidePriceButtonAction,
    showPreview,
    previewAction,
    showWishBtn,
    onClickWishBtn,
    fullHeight,
    showRating,
    showDiscount,
    showDiscountOnlyCashInfo,
    allowEmptyRating,
    containerWidth,
    onThumbnailClick,
    colorMinLength,
    attributeStyle,
    offerTimerProps
  } = _ref,
      product = _objectWithoutPropertiesLoose(_ref, ["adId", "hideLinkBorder", "onClick", "onMouseDown", "onView", "domain", "imgLazyLoading", "index", "lazyLoadingOffset", "showInCashInfo", "enableShadow", "enableHover", "geoLocTags", "allGeoLocTags", "hidePriceMode", "hidePrice", "hidePriceButtonAction", "showPreview", "previewAction", "showWishBtn", "onClickWishBtn", "fullHeight", "showRating", "percentBadge", "showDiscount", "showDiscountOnlyCashInfo", "allowEmptyRating", "containerWidth", "onThumbnailClick", "colorMinLength", "attributeStyle", "offerTimerProps"]);

  const {
    displayMode,
    title,
    price,
    image,
    installment,
    badges,
    rating,
    variationId,
    url,
    titleAs,
    loyalty,
    imageAltPrefix,
    isOnWishlist,
    imageWidth,
    imageHeight,
    shippingCost,
    shippingTag,
    brand,
    offerTags,
    ads,
    adsSellerId,
    thumbnails,
    restrictions,
    parsedAttributes,
    reward,
    tagCoupon
  } = product;
  const {
    absoluteUrl,
    href
  } = parseUrl({
    domain,
    url,
    adsSellerId
  });
  const {
    count,
    score
  } = rating;
  const isMagaluIndica = offerTags == null ? void 0 : offerTags.includes('magalu_indica');
  const isMercadoFull = offerTags == null ? void 0 : offerTags.includes('mercado_full');
  const badge = badges == null ? void 0 : badges.slice(-1)[0];

  const handleAddToWishlist = e => {
    e.stopPropagation();
    e.preventDefault();
    onClickWishBtn(e);
  };

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
    delay: 1000
  });
  useEffect(() => {
    if (inView) {
      onView(Object.assign({
        index,
        geoLocTags
      }, product));
    }
  }, [inView]);
  const imageAlt = imageAltPrefix ? `${imageAltPrefix}${product.title}` : product.title;
  const adIdValidated = (ads == null ? void 0 : ads.id) || undefined;
  const priceData = {
    hide: hidePriceMode === 'hide',
    hiddenPrice: hidePrice,
    displayMode,
    installment,
    showInCashInfo,
    showDiscount,
    showDiscountOnlyCashInfo,
    price,
    hidePriceButtonAction,
    product
  };
  const defaultTagProps = {
    autoSize: true,
    bg: 'primary',
    borderRadius: 6,
    fontSize: 1,
    fontWeight: 'medium',
    gridGap: 1,
    mb: 1,
    minWidth: 'unset',
    padding: '2px 8px'
  };
  return /*#__PURE__*/React.createElement(Container, {
    ref: ref,
    href: href,
    onClick: e => onClick(e, Object.assign({}, product, {
      index
    })),
    onMouseDown: e => onMouseDown(e, Object.assign({}, product, {
      index
    })),
    displayMode: displayMode,
    "data-testid": "product-card-container",
    "ads-click-event": adIdValidated,
    "ads-view-event": adIdValidated,
    "ads-impression-event": adIdValidated,
    redirect: href,
    hideLinkBorder: hideLinkBorder,
    enableShadow: enableShadow,
    enableHover: enableHover,
    fullHeight: fullHeight,
    containerWidth: containerWidth,
    index: index,
    "data-brand": brand == null ? void 0 : brand.slug
  }, /*#__PURE__*/React.createElement(CardHeader, {
    displayMode: displayMode,
    sponsored: ads == null ? void 0 : ads.sponsored,
    showWishBtn: showWishBtn,
    isLiked: isOnWishlist,
    handleAddToWishlist: handleAddToWishlist,
    isMagaluIndica: isMagaluIndica,
    isMercadoFull: isMercadoFull
  }), /*#__PURE__*/React.createElement(ImageContainer, {
    displayMode: displayMode
  }, displayMode === LIST && /*#__PURE__*/React.createElement(React.Fragment, null, isMagaluIndica && /*#__PURE__*/React.createElement(MagaluIndica, _extends({}, defaultTagProps, {
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      name: "FastDelivery",
      fill: "#fff",
      width: "16px",
      height: "16px"
    })
  }), "Full"), isMercadoFull && /*#__PURE__*/React.createElement(MercadoFull, _extends({}, defaultTagProps, {
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      name: "FastMarket",
      fill: "#fff",
      width: "16px",
      height: "16px"
    })
  }), "Mercado Full")), /*#__PURE__*/React.createElement(Image, {
    src: image,
    alt: imageAlt,
    width: imageWidth,
    height: imageHeight,
    title: title,
    lazy: imgLazyLoading,
    offset: lazyLoadingOffset,
    mb: 1
  }), /*#__PURE__*/React.createElement(Loyalty, {
    displayMode: displayMode,
    loyalty: loyalty,
    productUrl: absoluteUrl
  }), !!badge && /*#__PURE__*/React.createElement(Badge, {
    key: `${variationId}-badge-${badge.id}`,
    url: badge.imageUrl,
    tooltip: badge.tooltip,
    displayMode: displayMode,
    borderRadius: "medium"
  }), displayMode === LIST && parsedAttributes && /*#__PURE__*/React.createElement(AttributeList, {
    attributes: parsedAttributes,
    variations: thumbnails,
    colorVariant: "button",
    showTitle: false,
    colorMinLength: colorMinLength,
    gap: 1,
    style: attributeStyle,
    onVariationClick: onThumbnailClick
  })), showPreview && /*#__PURE__*/React.createElement(ButtonPreviewContainer, {
    "data-testid": "button-preview-container"
  }, /*#__PURE__*/React.createElement(ButtonPreview, {
    size: "small",
    m: '10px 0',
    "data-testid": "button-preview",
    onClick: e => {
      e.preventDefault();
      previewAction(product);
    }
  }, "Conferir")), /*#__PURE__*/React.createElement(Content, {
    displayMode: displayMode,
    "data-testid": "product-card-content"
  }, displayMode !== LIST && parsedAttributes && /*#__PURE__*/React.createElement(AttributeList, {
    attributes: parsedAttributes,
    variations: thumbnails,
    onVariationClick: onThumbnailClick,
    colorVariant: "button",
    showTitle: false,
    gap: 1,
    colorMinLength: colorMinLength,
    style: attributeStyle
  }), displayMode !== MINIATURE && /*#__PURE__*/React.createElement(OfferTimer, _extends({
    height: 20,
    fontSize: ['10px', 1],
    restrictions: restrictions,
    displayMode: displayMode
  }, offerTimerProps)), (ads == null ? void 0 : ads.sponsored) && displayMode !== MINIATURE && /*#__PURE__*/React.createElement(SponsoredEl, {
    place: "content"
  }, "Patrocinado"), displayMode !== MINIATURE && /*#__PURE__*/React.createElement(Title, {
    as: titleAs,
    displayMode: displayMode,
    "data-testid": "product-title"
  }, title), renderReviewVariations({
    displayMode,
    count,
    score,
    allowEmptyRating,
    showRating,
    format: 'score-count'
  }), /*#__PURE__*/React.createElement(GeoLocTags, {
    geoLocTags: geoLocTags,
    displayMode: displayMode
  }), /*#__PURE__*/React.createElement(ProductCardPrice, priceData), /*#__PURE__*/React.createElement(TagCoupon, {
    mt: 1,
    tags: tagCoupon
  }), /*#__PURE__*/React.createElement(ProductShipping, {
    displayMode: displayMode,
    costDescription: shippingCost.costDescription,
    cost: shippingCost.cost
  }), /*#__PURE__*/React.createElement(AllGeoLocTags, {
    allGeoLocTags: allGeoLocTags,
    displayMode: displayMode
  }), /*#__PURE__*/React.createElement(ProductCardShippingTag, {
    showShippingTags: displayMode !== MINIATURE,
    shippingTag: shippingTag,
    price: price
  }), !!reward && !!(reward != null && reward.active) && /*#__PURE__*/React.createElement(AlertText, {
    icon: reward == null ? void 0 : reward.icon,
    message: reward == null ? void 0 : reward.message,
    intent: reward == null ? void 0 : reward.intent,
    styleContainer: reward == null ? void 0 : reward.styleContainer,
    styleMessage: reward == null ? void 0 : reward.styleMessage
  })));
};

export default ProductCard;
process.env.NODE_ENV !== "production" ? ProductCard.propTypes = {
  adId: string,
  variationId: string,
  url: string,
  containerWidth: number,
  displayMode: string,
  domain: string,
  title: string,
  image: string,
  index: number,
  price: shape({}),
  installment: shape({
    amount: oneOfType([string, number]),
    paymentMethodDescription: string,
    quantity: oneOfType([string, number]),
    totalAmount: oneOfType([string, number])
  }),
  rating: shape({
    count: number,
    score: number
  }),
  badges: arrayOf(shape({
    imageUrl: string,
    id: string,
    tooltip: string
  })),
  brand: shape({
    slug: string
  }),
  geoLocTags: shape({
    title: string,
    icon: string
  }),
  allGeoLocTags: arrayOf(shape({
    title: string
  })),
  enableShadow: bool,
  enableHover: bool,
  onClick: func,
  onMouseDown: func,
  onView: func,
  imgLazyLoading: bool,
  fullHeight: bool,
  lazyLoadingOffset: number,
  showInCashInfo: bool,
  hideLinkBorder: bool,
  titleAs: string,
  imageAltPrefix: string,
  imageWidth: number,
  imageHeight: number,
  percentBadge: number,
  hidePriceMode: oneOfType(['blur', 'hide']),
  hidePrice: bool,
  hidePriceButtonAction: func,
  showPreview: bool,
  previewAction: func,
  isOnWishlist: bool,
  showWishBtn: bool,
  onClickWishBtn: func,
  showRating: bool,
  showDiscount: bool,
  showDiscountOnlyCashInfo: bool,
  shippingCost: shape({
    costDescription: string,
    cost: string
  }),
  shippingTag: shape({
    error: string,
    time: string,
    cost: number,
    complement: string
  }),
  ads: shape({
    id: string,
    sponsored: bool
  }),
  allowEmptyRating: bool,
  onThumbnailClick: func,
  colorMinLength: number,
  attributeStyle: shape({}),
  offerTimerProps: shape({})
} : void 0;
ProductCard.defaultProps = {
  variationId: '',
  url: '',
  displayMode: LIST,
  domain: MAGALU_DOMAIN,
  title: '',
  image: '',
  index: 0,
  price: {},
  installment: {},
  geoLocTags: {},
  allGeoLocTags: [],
  rating: {},
  badges: [],
  brand: {},
  onClick: () => {},
  onMouseDown: () => {},
  onView: () => {},
  imgLazyLoading: false,
  fullHeight: false,
  percentBadge: 30,
  lazyLoadingOffset: 50,
  showInCashInfo: true,
  hideLinkBorder: false,
  titleAs: 'h2',
  enableShadow: true,
  enableHover: false,
  imageAltPrefix: '',
  hidePriceMode: 'blur',
  hidePrice: false,
  hidePriceButtonAction: null,
  showPreview: false,
  previewAction: () => {},
  isOnWishlist: false,
  showWishBtn: false,
  onClickWishBtn: () => {},
  showRating: true,
  showDiscount: false,
  showDiscountOnlyCashInfo: true,
  shippingCost: {},
  ads: {},
  shippingTag: {},
  allowEmptyRating: false,
  onThumbnailClick: null,
  colorMinLength: 3,
  attributeStyle: {
    width: '42px',
    height: '42px',
    bg: 'background.lighter',
    color: 'background.primary',
    shouldHover: true
  },
  offerTimerProps: {}
};